/**
 * Secure storage utility — NON-SENSITIVE data only.
 *
 * ⚠️  SECURITY RULE: NEVER store auth tokens (access_token, refresh_token)
 *     or user credentials in localStorage / sessionStorage.
 *
 * Auth tokens are managed as follows:
 *  - Refresh token → HttpOnly cookie (set by backend)
 *  - Access token  → React state (in-memory only, via AuthContext)
 *  - User data     → React state (in-memory only, refreshed via /auth/me)
 *
 * This utility may be used for non-sensitive UI preferences (theme, locale,
 * sidebar state, etc.) and for legacy cleanup.
 */

const PREFIX = '__nny_';

// ── Blocked keys (auth-sensitive) ──
const BLOCKED_KEYS = new Set(['token', 'refresh_token', 'user', 'access_token']);

function _guardKey(key) {
  if (BLOCKED_KEYS.has(key)) {
    console.warn(
      `[tokenStorage] Blocked attempt to store sensitive key "${key}" in localStorage. ` +
      'Auth data must stay in memory (React state) + HttpOnly cookies.'
    );
    return false;
  }
  return true;
}

export const tokenStorage = {
  /**
   * Store a non-sensitive value.
   * @param {string} key - Storage key (will be prefixed)
   * @param {string} value - Value to store
   */
  set(key, value) {
    if (!_guardKey(key)) return;
    try {
      localStorage.setItem(`${PREFIX}${key}`, value);
    } catch (e) {
      console.error('tokenStorage.set failed:', e);
    }
  },

  /**
   * Retrieve a stored value.
   * @param {string} key - Storage key (will be prefixed)
   * @returns {string|null}
   */
  get(key) {
    try {
      return localStorage.getItem(`${PREFIX}${key}`);
    } catch (e) {
      console.error('tokenStorage.get failed:', e);
      return null;
    }
  },

  /**
   * Remove a stored value.
   * @param {string} key - Storage key (will be prefixed)
   */
  remove(key) {
    try {
      localStorage.removeItem(`${PREFIX}${key}`);
    } catch (e) {
      console.error('tokenStorage.remove failed:', e);
    }
  },

  /**
   * Store a JSON-serializable object (non-sensitive only).
   */
  setJSON(key, obj) {
    if (!_guardKey(key)) return;
    try {
      localStorage.setItem(`${PREFIX}${key}`, JSON.stringify(obj));
    } catch (e) {
      console.error('tokenStorage.setJSON failed:', e);
    }
  },

  /**
   * Retrieve and parse a stored JSON object.
   * @returns {object|null}
   */
  getJSON(key) {
    const raw = this.get(key);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      this.remove(key); // Corrupted data — clean up
      return null;
    }
  },

  /**
   * Remove ALL Nannyou data from storage (including legacy auth keys).
   * Called on logout to ensure complete cleanup.
   */
  clearAll() {
    try {
      const keys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(PREFIX)) {
          keys.push(k);
        }
      }
      keys.forEach((k) => localStorage.removeItem(k));
    } catch (e) {
      console.error('tokenStorage.clearAll failed:', e);
    }
  },
};
